import { Injectable } from '@angular/core';
import { BehaviorSubject, forkJoin, map, Observable, of, tap } from 'rxjs';
import { ConsentTaxFolderUI } from 'src/app/interfaces/consent-tax-folder.interface';
import {
	CustomerDataUI,
	JourneyMetadataUI,
	RequestValidateCustomerData,
	ValidateCustomerDataUI,
} from 'src/app/interfaces/customer-data.interface';
import {
	GenerateSendOtpUI,
	RequestGenerateSendOtp,
} from 'src/app/interfaces/generate-send-otp.interface';
import {
	InitialParametersModel,
	InitialParametersUI,
} from 'src/app/interfaces/initial-parameters.interface';
import { OtpValidationUI, RequestOtpValidation } from 'src/app/interfaces/otp-validation.interface';
import { RequestTaxFolder, ResponseTaxFolderUI } from 'src/app/interfaces/tax-folder.interface';
import { mapper } from 'src/app/utils/mapper';
import { ApiService } from '../api/api.service';
import { EncryptionService } from '../encryption/encryption.service';
import { IdsUI, PurposesByType, PurposesUI } from 'src/app/interfaces/get-purposes';
import { RegisterPurposesRequest } from 'src/app/interfaces/register-purposes';

@Injectable({
	providedIn: 'root',
})
export class StoreService {
	private purposesSubject = new BehaviorSubject<PurposesByType | null>(null);
	readonly purposes$ = this.purposesSubject.asObservable();

	private journeyMetadataSubject = new BehaviorSubject<JourneyMetadataUI | null>(null);
	readonly journeyMetadata$ = this.journeyMetadataSubject.asObservable();

	private initialParametersSubject = new BehaviorSubject<InitialParametersUI | null>(null);
	readonly initialParameters$ = this.initialParametersSubject.asObservable();

	private customerDataSubject = new BehaviorSubject<CustomerDataUI | null>(null);
	readonly customerData$ = this.customerDataSubject.asObservable();

	private otpSubject = new BehaviorSubject<GenerateSendOtpUI | null>(null);
	readonly otp$ = this.otpSubject.asObservable();

	private consentSubject = new BehaviorSubject<ConsentTaxFolderUI | null>(null);
	readonly consent$ = this.consentSubject.asObservable();

	constructor(
		private readonly apiService: ApiService,
		private encryptionService: EncryptionService
	) {}

	get journeyMetadata(): JourneyMetadataUI | null {
		return this.journeyMetadataSubject.value;
	}

	setCustomerData(customerData: CustomerDataUI): void {
		this.customerDataSubject.next(customerData);
	}

	initialParameters(product: string): Observable<InitialParametersModel> {
		if (this.journeyMetadataSubject.value && this.initialParametersSubject.value) {
			return of({
				...this.initialParametersSubject.value,
				simulatorType: this.journeyMetadataSubject.value.productType,
			});
		}

		return this.apiService.initialParameters(product).pipe(
			map(resp => mapper.initialParameters(resp)),
			tap(mapped => {
				this.journeyMetadataSubject.next({ productType: mapped.simulatorType });
				this.initialParametersSubject.next(this.extractSimulatorType(mapped));
			})
		);
	}

	validateData(req: RequestValidateCustomerData): Observable<ValidateCustomerDataUI> {
		return this.apiService.validateCustomerData(req).pipe(
			map(resp => mapper.validateData(resp)),
			tap((response: ValidateCustomerDataUI) => {
				this.patchJourneyMetadata({ uuid: response.uuid, id: response.id });
			})
		);
	}

	generateConsentTaxFolder(rut: string): Observable<ConsentTaxFolderUI> {
		return this.apiService.consentTaxFolder({ rut }).pipe(
			tap(({ certificado }) => this.encryptionService.initializePublicKey(certificado)),
			map(resp => mapper.dataConsent(resp)),
			tap((mapped: ConsentTaxFolderUI) => {
				this.consentSubject.next(mapped);
			})
		);
	}

	taxFolder(req: RequestTaxFolder): Observable<ResponseTaxFolderUI> {
		return this.apiService.taxFolder(req).pipe(map(resp => mapper.taxFolder(resp)));
	}

	generateSendOtp(
		req: RequestGenerateSendOtp,
		skipLoader: boolean = false
	): Observable<GenerateSendOtpUI> {
		return this.apiService.generateAndSendOtp(req, skipLoader).pipe(
			map(resp => mapper.otpGenerated(resp)),
			tap((mapped: GenerateSendOtpUI) => {
				if (req.nuevoDestino) this.updateCustomerPhone(req.nuevoDestino);
				this.otpSubject.next(mapped);
			})
		);
	}

	validationOtp(req: RequestOtpValidation): Observable<OtpValidationUI> {
		return this.apiService.validateOtp(req).pipe(map(mapper.otpValidation));
	}

	private extractSimulatorType(parameters: InitialParametersModel): InitialParametersUI {
		const { simulatorType, ...parametersUI } = parameters;
		return parametersUI;
	}

	private updateCustomerPhone(newPhone: string): void {
		const currentCustomer = this.customerDataSubject.value;
		if (currentCustomer) {
			this.customerDataSubject.next({
				...currentCustomer,
				phoneNumber: newPhone,
			});
		}
	}

	private patchJourneyMetadata(patch: Partial<JourneyMetadataUI>): void {
		const current = this.journeyMetadataSubject.value;
		if (!current) return;

		this.journeyMetadataSubject.next({
			...current,
			...patch,
		});
	}

	getPurposes(ids: IdsUI): Observable<PurposesByType> {
		return forkJoin({
			pj: this.apiService
				.purposes({ idFormulario: ids.pj })
				.pipe(map((resp): PurposesUI => mapper.purposes(resp))),
			png: this.apiService
				.purposes({ idFormulario: ids.png })
				.pipe(map((resp): PurposesUI => mapper.purposes(resp))),
		}).pipe(tap(mapped => this.purposesSubject.next(mapped)));
	}

	registerPurposes(request: RegisterPurposesRequest): Observable<void> {
		return this.apiService.registerPurposes(request);
	}
}
